import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Button, Card, Field, HeroSelectField, Input, LoadingScreen, RadioOptions } from '../components/Ui';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useToast } from '../contexts/ToastContext';
import { currentPosition } from '../lib/geo';

type EmployeeLoginRole = 'reception' | 'kitchen' | 'delivery';

export default function EmployeeLoginPage() {
  const { tenant, loading, error } = useTenant();
  const { user, employeeLogin, waiterLogin } = useAuth();
  const { show } = useToast();
  const navigate = useNavigate();
  const [branchId, setBranchId] = useState('');
  const [role, setRole] = useState<EmployeeLoginRole>('reception');
  const [pin, setPin] = useState('');
  const [waiterUsername, setWaiterUsername] = useState('');
  const [waiterPassword, setWaiterPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const selectedBranch = tenant?.branches.find((branch) => branch.id === branchId) ?? null;
  const roleOptions = [
    { value: 'reception', label: 'Recepción' },
    { value: 'kitchen', label: 'Cocina' },
    { value: 'delivery', label: 'Reparto' },
  ] as const;

  if (user) {
    const target = user.role === 'reception' ? '/dashboard/reception' : user.role === 'kitchen' ? '/dashboard/kitchen' : user.role === 'delivery' ? '/dashboard/delivery' : user.role === 'waiter' ? '/dashboard/waiter' : user.role === 'super_admin' ? '/admin' : '/dashboard';
    return <Navigate to={target} replace />;
  }
  if (loading) return <LoadingScreen label="Cargando sucursales…" />;
  if (!tenant || error) return <main className="app-brand-shell grid min-h-screen place-items-center p-4"><Card><h1 className="font-black text-[var(--app-brand-ink)]">Negocio no disponible</h1><p className="mt-2 text-sm text-[var(--app-brand-muted)]">{error}</p><Link to="/"><Button className="mt-4">Volver</Button></Link></Card></main>;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!branchId) { show('Selecciona una sucursal.', 'error'); return; }
    setBusy(true);
    try {
      let location: { lat: number; lng: number; accuracy: number } | undefined;
      if (selectedBranch?.requiresGeolocation) {
        const position = await currentPosition({ enableHighAccuracy: true, timeout: 20_000, maximumAge: 0 });
        location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        };
      }
      const next = await employeeLogin({ branchId, role, pin, location });
      navigate(next.role === 'reception' ? '/dashboard/reception' : next.role === 'kitchen' ? '/dashboard/kitchen' : '/dashboard/delivery', { replace: true });
    } catch (caught) {
      const rawCode = typeof caught === 'object' && caught !== null && 'code' in caught
        ? (caught as { code?: unknown }).code
        : undefined;
      const geolocationCode = typeof rawCode === 'number' ? rawCode : null;
      const message = geolocationCode !== null
        ? geolocationCode === 1 ? 'Debes permitir la ubicación para iniciar sesión.' : 'No se pudo obtener una ubicación precisa.'
        : caught instanceof Error ? caught.message : 'No se pudo iniciar sesión.';
      show(message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const submitWaiter = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await waiterLogin(waiterUsername, waiterPassword);
      navigate('/dashboard/waiter', { replace: true });
    } catch (caught) {
      show(caught instanceof Error ? caught.message : 'No se pudo iniciar sesión como mesero.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="app-brand-shell grid min-h-screen place-items-center p-4">
      <Card className="w-full max-w-md p-7 sm:p-9">
        <div className="flex items-center gap-3">
          {tenant.logoUrl ? <img src={tenant.logoUrl} alt="" className="h-12 w-12 rounded-xl object-cover" /> : <div className="grid h-12 w-12 place-items-center rounded-xl bg-[var(--app-brand-ink)] text-xl text-white">M</div>}
          <div><h1 className="font-black text-[var(--app-brand-ink)]">{tenant.name}</h1><p className="text-xs text-[var(--app-brand-muted)]">Acceso de empleados</p></div>
        </div>
        <form onSubmit={submit} className="mt-6 grid gap-4">
          <HeroSelectField
            label="Sucursal"
            isRequired
            placeholder="Selecciona una sucursal"
            value={branchId || null}
            onChange={(value) => setBranchId(String(value ?? ''))}
            options={tenant.branches.map((branch) => ({ id: branch.id, label: branch.name }))}
          />
          <RadioOptions<EmployeeLoginRole> label="Área" value={role} onChange={setRole} options={roleOptions} orientation="horizontal" />
          <Field label="PIN de 4 dígitos" hint={selectedBranch?.requiresGeolocation ? 'La ubicación se comprobará al entrar.' : 'Esta sucursal no exige geolocalización.'}><Input required inputMode="numeric" pattern="[0-9]{4}" maxLength={4} value={pin} onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 4))} className="text-center text-2xl tracking-[.5em]" /></Field>
          <Button type="submit" busy={busy} className="w-full">{selectedBranch?.requiresGeolocation ? 'Validar PIN y ubicación' : 'Validar PIN'}</Button>
        </form>
        <div className="my-6 border-t" />
        <form onSubmit={submitWaiter} className="grid gap-4">
          <div><h2 className="font-black text-[var(--app-brand-ink)]">Acceso de meseros</h2><p className="text-xs text-[var(--app-brand-muted)]">Usuario individual asignado por el negocio.</p></div>
          <Field label="Usuario"><Input required value={waiterUsername} onChange={(event) => setWaiterUsername(event.target.value)} /></Field>
          <Field label="Contraseña"><Input required type="password" minLength={8} value={waiterPassword} onChange={(event) => setWaiterPassword(event.target.value)} /></Field>
          <Button type="submit" busy={busy} className="w-full">Entrar como mesero</Button>
        </form>
        <Link to="/" className="mt-5 block text-center text-sm font-semibold text-[var(--app-brand-muted)]">Volver al menú</Link>
        {import.meta.env.DEV && <p className="mt-4 rounded-xl bg-amber-50 p-3 text-xs text-amber-800">Demo: recepción 1111 · cocina 2222 · reparto 3333. La sucursal demo tiene geolocalización desactivada.</p>}
      </Card>
    </main>
  );
}
